import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Star, Video, Upload, Loader2, CheckCircle2, RefreshCw,
  Circle, Square, ShieldCheck, Heart, Copy, ExternalLink
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_SECONDS = 120;

const pickMimeType = () => {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  if (typeof MediaRecorder === "undefined") return "";
  return candidates.find((c) => MediaRecorder.isTypeSupported(c)) || "";
};

const mimeToExt = (type = "") => {
  if (type.includes("mp4")) return "mp4";
  if (type.includes("quicktime")) return "mov";
  if (type.includes("ogg")) return "ogv";
  return "webm";
};

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1.5" data-testid="testimonial-star-picker">
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" onClick={() => onChange(i)} data-testid={`star-${i}`} className="transition-transform hover:scale-110">
        <Star className={`h-8 w-8 ${i <= value ? "text-[#A87608] fill-[#D4A017]" : "text-[#3a4a6d]"}`} />
      </button>
    ))}
  </div>
);

export default function TestimonialSubmit() {
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState("");

  const [mode, setMode] = useState("record"); // record | upload
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [googleUrl, setGoogleUrl] = useState("");

  const liveVideoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      stopStream();
      if (timerRef.current) clearInterval(timerRef.current);
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch(`${API}/api/site/reviews`)
      .then((r) => r.json())
      .then((d) => setGoogleUrl(d?.settings?.google_reviews_url || ""))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (recording && liveVideoRef.current && streamRef.current) {
      liveVideoRef.current.srcObject = streamRef.current;
      liveVideoRef.current.muted = true;
      liveVideoRef.current.play().catch(() => {});
    }
  }, [recording]);

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message.trim());
      toast.success("Copied! Now paste it into Google 👍");
    } catch {
      toast.error("Couldn't copy — please type your words on Google");
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const clearVideo = () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoBlob(null);
    setVideoPreviewUrl("");
    setElapsed(0);
  };

  const startRecording = async () => {
    clearVideo();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        liveVideoRef.current.muted = true;
        await liveVideoRef.current.play().catch(() => {});
      }
      const mimeType = pickMimeType();
      const mr = new MediaRecorder(stream, mimeType ? { mimeType, videoBitsPerSecond: 2500000 } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "video/webm" });
        setVideoBlob(blob);
        setVideoPreviewUrl(URL.createObjectURL(blob));
        stopStream();
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          if (prev + 1 >= MAX_SECONDS) { stopRecording(); return MAX_SECONDS; }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      toast.error("Couldn't access camera/mic. Please allow permissions or upload a video instead.");
      setMode("upload");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const handleUploadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file"); return; }
    if (file.size > 120 * 1024 * 1024) { toast.error("Video is too large. Please keep it under 2 minutes."); return; }
    clearVideo();
    setVideoBlob(file);
    setVideoPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (!videoBlob) { toast.error("Please record or upload a video"); return; }
    if (recording) stopRecording();

    setSubmitting(true);
    setProgress(0);

    const fd = new FormData();
    fd.append("customer_name", name.trim());
    fd.append("customer_city", city.trim());
    fd.append("rating", String(rating));
    fd.append("message", message.trim());
    if (videoBlob) {
      const ext = mimeToExt(videoBlob.type);
      fd.append("video", videoBlob, `testimonial.${ext}`);
    }

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}/api/testimonials/submit`, true);
    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) setProgress(Math.round((evt.loaded / evt.total) * 100));
    };
    xhr.onload = () => {
      setSubmitting(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        setDone(true);
      } else {
        let msg = "Something went wrong. Please try again.";
        try { msg = JSON.parse(xhr.responseText).detail || msg; } catch {}
        toast.error(msg);
      }
    };
    xhr.onerror = () => { setSubmitting(false); toast.error("Network error. Please try again."); };
    xhr.send(fd);
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (done) {
    return (
      <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="testimonial-thank-you">
          <div className="h-20 w-20 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold mb-3">Thank you, {name.split(" ")[0] || "friend"}! 🙏</h1>
          <p className="text-[#33436B] mb-3">
            Your kind words mean the world to us. It's a privilege to be trusted with your family's protection.
          </p>
          <p className="text-[#33436B] mb-6">
            And remember — whenever you or your loved ones need any help with insurance, we'll always be there and do our very best for you. 💛
          </p>
          <p className="text-[#A87608] text-sm font-semibold mb-8">— Prashant, Kavach Box</p>

          {googleUrl && (
            <div className="bg-white border border-[#C9D4E6] rounded-2xl p-5 sm:p-6 mb-6 shadow-sm text-left" data-testid="google-review-nudge">
              <div className="flex items-start gap-3">
                <span className="h-9 w-9 shrink-0 rounded-full bg-white border border-[#C9D4E6] flex items-center justify-center text-lg font-bold" style={{ fontFamily: "Arial, sans-serif" }} aria-hidden>
                  <span style={{ color: "#4285F4" }}>G</span>
                </span>
                <div>
                  <h2 className="text-base font-bold text-[#1B2A4A]">One small favour 💛</h2>
                  <p className="text-sm text-[#33436B] mt-1">
                    A quick Google review helps other families find honest insurance advice. It takes 20 seconds{message.trim() ? " — just copy your words below and paste them on Google." : "."}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2.5 mt-4">
                {message.trim() && (
                  <button
                    onClick={copyMessage}
                    data-testid="copy-message-btn"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] text-[#1B2A4A] text-sm font-medium transition-colors"
                  >
                    <Copy className="h-4 w-4" /> Copy my words
                  </button>
                )}
                <a
                  href={googleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="post-on-google-btn"
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#4285F4] hover:bg-[#3b78e0] text-white text-sm font-semibold transition-colors"
                >
                  <ExternalLink className="h-4 w-4" /> Post on Google
                </a>
              </div>
            </div>
          )}

          <button
            onClick={() => { setDone(false); setName(""); setCity(""); setRating(5); setMessage(""); clearVideo(); }}
            className="text-[#A87608] font-medium hover:underline"
            data-testid="submit-another-btn"
          >
            Submit another testimonial
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A]">
      {/* Branded header */}
      <header className="border-b border-[#C9D4E6] bg-white/60">
        <div className="max-w-2xl mx-auto px-4 py-5 flex items-center gap-3">
          <div>
            <img src="/kavachbox-logo-light.png" alt="Kavach Box" className="h-9 w-auto" />
            <p className="text-[9px] text-[#3A4557] mt-0.5">A part of Shree Finserv Group</p>
          </div>
          <a
            href="https://www.beshak.org/advisor/shree-finserv/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="beshak-verify-link"
            className="ml-auto flex items-center gap-2 text-xs text-[#33436B] hover:text-[#1B2A4A] transition-colors"
            title="Verify Prashant on Beshak.org"
          >
            <ShieldCheck className="h-4 w-4 text-[#A87608]" />
            Beshak Verified Advisor
            <ExternalLink className="h-3 w-3 opacity-60" />
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 pb-16">
        {/* Primary CTA: leave a Google review */}
        {googleUrl && (
          <div className="bg-white border border-[#C9D4E6] rounded-2xl p-5 sm:p-7 mb-6 shadow-sm text-center" data-testid="google-review-cta">
            <span className="inline-flex h-12 w-12 rounded-full bg-white border border-[#C9D4E6] items-center justify-center text-2xl font-bold mb-3" style={{ fontFamily: "Arial, sans-serif" }} aria-hidden>
              <span style={{ color: "#4285F4" }}>G</span>
            </span>
            <div className="flex items-center justify-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((i) => (<Star key={i} className="h-5 w-5 text-[#A87608] fill-[#D4A017]" />))}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A]">Happy with the guidance from Prashant?</h1>
            <p className="text-[#33436B] text-sm mt-2 max-w-md mx-auto">
              A quick review on Google takes 30 seconds and helps other families find honest insurance advice. It means the most to us. 💛
            </p>
            <a
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="review-on-google-btn"
              className="mt-4 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#4285F4] hover:bg-[#3b78e0] text-white font-semibold transition-colors"
            >
              <ExternalLink className="h-4 w-4" /> Review us on Google
            </a>
          </div>
        )}

        {/* Divider → secondary private/video path */}
        <div className="flex items-center gap-3 mb-6" data-testid="testimonial-divider">
          <div className="flex-1 h-px bg-[#C9D4E6]" />
          <span className="text-xs text-[#5a6a8d] font-medium whitespace-nowrap">or leave a private note / video</span>
          <div className="flex-1 h-px bg-[#C9D4E6]" />
        </div>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4A017]/15 text-[#A87608] text-xs font-semibold mb-4">
            <Heart className="h-3.5 w-3.5" /> Share your experience
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Or record a quick video for us</h2>
          <p className="text-[#33436B]">A short video testimonial we may feature on the Kavach Box website — just your name, city and a quick recording.</p>
        </div>

        <div className="space-y-6 bg-white/50 border border-[#C9D4E6] rounded-2xl p-5 sm:p-7">
          {/* Rating */}
          <div className="flex flex-col items-center gap-3">
            <label className="text-sm font-medium text-[#2E3B57]">Your rating</label>
            <StarPicker value={rating} onChange={setRating} />
          </div>

          {/* Name + City */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">Your name *</label>
              <input
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Rakesh Mehta"
                data-testid="testimonial-name-input"
                className="w-full px-3 py-2.5 bg-[#F4F6FA] border border-[#C9D4E6] rounded-xl text-[#1B2A4A] placeholder-[#5a6a8d] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/40"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">City (optional)</label>
              <input
                value={city} onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Ahmedabad"
                data-testid="testimonial-city-input"
                className="w-full px-3 py-2.5 bg-[#F4F6FA] border border-[#C9D4E6] rounded-xl text-[#1B2A4A] placeholder-[#5a6a8d] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/40"
              />
            </div>
          </div>

          {/* Video */}
          <div>
            <label className="text-sm font-medium text-[#2E3B57] mb-2 block">Record a short video testimonial</label>
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => { setMode("record"); clearVideo(); }}
                data-testid="mode-record-btn"
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-colors ${mode === "record" ? "bg-[#D4A017] text-[#0a1628]" : "bg-[#EDF1F8] text-[#33436B]"}`}
              >
                <Video className="h-4 w-4" /> Record
              </button>
              <button
                onClick={() => { setMode("upload"); stopRecording(); clearVideo(); stopStream(); }}
                data-testid="mode-upload-btn"
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-colors ${mode === "upload" ? "bg-[#D4A017] text-[#0a1628]" : "bg-[#EDF1F8] text-[#33436B]"}`}
              >
                <Upload className="h-4 w-4" /> Upload
              </button>
            </div>

            <div className="rounded-xl overflow-hidden border border-[#C9D4E6] bg-black/40 relative">
              {/* Recorded / uploaded preview */}
              {videoPreviewUrl ? (
                <video src={videoPreviewUrl} controls autoPlay muted playsInline className="w-full max-h-72 bg-black" data-testid="video-preview" />
              ) : mode === "record" ? (
                <>
                  <video ref={liveVideoRef} autoPlay playsInline muted className="w-full max-h-72 bg-black object-cover" style={{ transform: "scaleX(-1)" }} data-testid="live-preview" />
                  {recording ? (
                    <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-red-600/95 text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="rec-indicator">
                      <span className="h-2 w-2 rounded-full bg-white animate-pulse" /> REC {fmt(elapsed)}
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-white/75 text-sm pointer-events-none px-4 text-center">
                      <Video className="h-7 w-7" />
                      <span>Tap "Start recording" — you'll see yourself here</span>
                    </div>
                  )}
                </>
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 py-10 cursor-pointer text-[#33436B] hover:text-[#1B2A4A]">
                  <Upload className="h-8 w-8" />
                  <span className="text-sm">Tap to choose a video from your device</span>
                  <input type="file" accept="video/*" className="hidden" onChange={handleUploadFile} data-testid="video-file-input" />
                </label>
              )}
            </div>
            {videoPreviewUrl && mode === "record" && (
              <p className="text-center text-xs text-[#33436B] mt-2">Here's your recording — tap play to review it, then submit or re-record.</p>
            )}

            {/* Controls */}
            <div className="flex items-center justify-center gap-3 mt-3">
              {mode === "record" && !videoPreviewUrl && (
                recording ? (
                  <button onClick={stopRecording} data-testid="stop-record-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-medium">
                    <Square className="h-4 w-4 fill-white" /> Stop • {fmt(elapsed)}
                  </button>
                ) : (
                  <button onClick={startRecording} data-testid="start-record-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-medium">
                    <Circle className="h-4 w-4 fill-white" /> Start recording
                  </button>
                )
              )}
              {videoPreviewUrl && (
                <button onClick={() => { clearVideo(); if (mode === "record") { /* stay */ } }} data-testid="retake-video-btn" className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#EDF1F8] hover:bg-[#E2E9F4] text-[#1B2A4A] text-sm">
                  <RefreshCw className="h-4 w-4" /> {mode === "record" ? "Re-record" : "Choose another"}
                </button>
              )}
            </div>
            {mode === "record" && !videoPreviewUrl && (
              <p className="text-center text-xs text-[#33436B] mt-2">Up to 2 minutes. You can re-record before submitting.</p>
            )}
          </div>

          {/* Submit */}
          {submitting && videoBlob && (
            <div className="w-full h-2 bg-[#EDF1F8] rounded-full overflow-hidden">
              <div className="h-full bg-[#D4A017] transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-testimonial-btn"
            className="w-full py-3 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] text-[#0a1628] font-semibold hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (<><Loader2 className="h-5 w-5 animate-spin" /> {videoBlob ? `Uploading ${progress}%` : "Submitting..."}</>) : "Submit Testimonial"}
          </button>
          <p className="text-center text-xs text-[#33436B]">Your testimonial may be featured on Kavach Box with your name & city. By submitting, you consent to this.</p>
        </div>
      </main>
    </div>
  );
}
